import axios from "axios";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import { nuclearn_api_url } from "src/App";

import type { Collection, CollectionRecordsPage } from "./types";

// === COLLECTIONS LISTING ===

const getCollections = (): Promise<Collection[]> =>
  axios.get(`${nuclearn_api_url}/datasets/collections`).then((res) => res.data);

/**
 * Collections readable by the caller (`GET /datasets/collections`, MRE
 * #30594): datasets flagged `is_collection`, already scoped to the caller's
 * accessible set and ordered by name server-side. The set is admin-curated
 * and small, so it arrives unpaginated.
 */
export const useCollections = ({ enabled = true } = {}) =>
  useQuery(["collections"], getCollections, {
    enabled,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });

// === SET / UNSET THE COLLECTION FLAG ===

const setDatasetCollectionFlag = ({
  datasetId,
  isCollection,
}: {
  datasetId: number;
  isCollection: boolean;
}): Promise<{ id: number; is_collection: boolean }> =>
  axios
    .put(`${nuclearn_api_url}/datasets/${datasetId}/collection`, {
      is_collection: isCollection,
    })
    .then((res) => res.data);

/**
 * Flags a dataset as a Library collection, or clears the flag (#2460).
 *
 * Admin-curated: the endpoint authorizes on `admin:write` AND per-object
 * dataset manage, so an owner without the admin role is denied. Callers gate
 * the affordance on both axes rather than letting the request 403.
 *
 * Invalidates the collections listing so the Library pane's group reflects the
 * change without a reload — the group hides entirely when the caller can read
 * none, so the first flag in an org is what makes it appear at all. Also busts
 * the dataset itself, which carries `is_collection`.
 */
export const useSetDatasetCollectionFlag = () => {
  const queryClient = useQueryClient();
  return useMutation(setDatasetCollectionFlag, {
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(["collections"]);
      queryClient.invalidateQueries(["dataset", variables.datasetId]);
    },
  });
};

/** One collection by id, resolved from the (small, cached) listing. */
export const useCollection = (collectionId: number | null | undefined) => {
  const query = useCollections({ enabled: collectionId != null });
  return {
    ...query,
    data: query.data?.find((c) => c.id === collectionId),
  };
};

// === COLLECTION RECORDS ===

export const COLLECTION_RECORDS_PAGE_SIZE = 50;

/**
 * Columns the server can page with a keyset cursor. Mirrors
 * `dataset_crud.SORTABLE_BASE_COLUMNS` in MRE, which is also what that layer
 * resolves a sort column from — a name missing there is read as a key into the
 * JSONB `data` blob instead, which both changes the ordering and drops the
 * cursor. Sorting on such a field makes the endpoint fall back to OFFSET
 * paging, so the cursor this client sends has to change with the column.
 */
const KEYSET_SORT_COLUMNS = new Set([
  "id",
  "create_date",
  "source_uid",
  "updated_date",
  "last_update_date",
  "version_number",
]);

/**
 * The server's sort key for a record's display title — its own
 * `dataset_crud.RECORD_TITLE_SORT_KEY`, a pseudo-column rather than a table
 * column or a `data` field. Underscored there so it cannot collide with a real
 * JSONB key, and absent from the keyset set above for the same reason a `data`
 * field is: the server pages it by offset.
 */
export const RECORD_TITLE_SORT_KEY = "_record_title";

/** Whether `sort` (a `-`-prefixed field name) still supports the `after` cursor. */
export const sortSupportsKeyset = (sort?: string): boolean =>
  sort === undefined || KEYSET_SORT_COLUMNS.has(sort.replace(/^-/, ""));

/**
 * One page of a collection's records.
 *
 * `cursor` is an id under keyset paging and a zero-based page index otherwise,
 * because the server accepts only one of the two for a given sort — see
 * `sortSupportsKeyset`.
 */
const getCollectionRecordsPage = (
  collectionId: number,
  cursor?: number,
  sort?: string,
): Promise<CollectionRecordsPage> =>
  axios
    .get(
      `${nuclearn_api_url}/datasets/${collectionId}/records-with-embedded-chunk-count`,
      {
        params: {
          limit: COLLECTION_RECORDS_PAGE_SIZE,
          ...(sort ? { sort } : {}),
          ...(cursor == null
            ? {}
            : sortSupportsKeyset(sort)
              ? { after: cursor }
              : // 1-indexed, and it is the page AFTER the one just loaded.
                { page: cursor + 1 }),
        },
      },
    )
    .then((res) => res.data);

/**
 * A collection's records, loaded incrementally (collections run to hundreds
 * of thousands of rows). First page by offset, then keyset via the `after`
 * cursor — O(log n) at any depth, no OFFSET scans. Default server sort (id)
 * keeps the keyset stable.
 *
 * `sort` is a `-`-prefixed field name the server orders by, so a sorted list
 * is sorted across every page rather than only the pages already fetched. It
 * is part of the query key: changing it re-fetches from the first page, since
 * appending a differently-ordered page to the ones already held would produce
 * a list that is sorted nowhere.
 *
 * A sort the keyset cannot express (any `data` field) pages by index instead,
 * which is what the server falls back to for those columns anyway.
 */
export const useCollectionRecords = (
  collectionId: number | null | undefined,
  sort?: string,
) =>
  useInfiniteQuery(
    ["collection-records", collectionId, sort ?? null],
    ({ pageParam }: { pageParam?: number }) =>
      getCollectionRecordsPage(collectionId as number, pageParam, sort),
    {
      enabled: collectionId != null,
      refetchOnWindowFocus: false,
      // react-query v3 replays an infinite query's pages one request at a
      // time on refetch, so an unstaled query turns a record → back → record
      // loop into N sequential requests. Matches useCollections/useFolderChats.
      staleTime: 60 * 1000,
      getNextPageParam: (lastPage, pages) => {
        if (!lastPage?.meta?.hasMore) return undefined;
        // Page index, not a record id: the server ignores `after` under a
        // JSONB sort, so a cursor would silently re-serve page one forever.
        if (!sortSupportsKeyset(sort)) return pages.length;
        // `data` is optional-chained for the same reason `meta` is: this runs
        // against whatever the server actually returned, and a page missing
        // its rows would otherwise throw inside react-query rather than
        // simply ending the pagination.
        return (
          lastPage.meta.nextCursor ??
          lastPage.data?.[lastPage.data.length - 1]?.id ??
          undefined
        );
      },
    },
  );
